/**
 * Pure schema.org JSON-LD builders shared across pages. Each returns a
 * plain object that `useJsonLd` serialises into a
 * `<script type="application/ld+json">` block. Keeping the shapes here
 * (rather than inline in components) keeps organisation details and
 * URL handling consistent across listing, article, and breadcrumb data.
 */

import type { Property } from "@/types/property";
import type { InsightArticle } from "@/lib/insights";
import { formatListingTitle } from "@/lib/format";
import { propertyPath } from "@/lib/property-url";

const ORG_ID = "https://hivehomes.ae/#organization";

function origin(): string {
  return typeof window !== "undefined" ? window.location.origin : "https://hivehomes.ae";
}

/** Resolve a possibly-relative asset path to an absolute URL. */
export function absoluteUrl(input: string): string {
  if (/^https?:\/\//i.test(input)) return input;
  if (input.startsWith("/")) return `${origin()}${input}`;
  return `${origin()}/${input}`;
}

interface Crumb {
  name: string;
  path: string;
}

export function breadcrumbLd(items: Crumb[]): object {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: `${origin()}${item.path}`,
    })),
  };
}

const LISTING_AVAILABILITY: Record<Property["status"], string> = {
  ready: "https://schema.org/InStock",
  "off-plan": "https://schema.org/PreOrder",
  rented: "https://schema.org/SoldOut",
  sold: "https://schema.org/SoldOut",
};

export function realEstateListingLd(property: Property, images: string[]): object {
  const title = formatListingTitle(property);
  const url = `${origin()}${propertyPath(property)}`;

  return {
    "@context": "https://schema.org",
    "@type": "RealEstateListing",
    "@id": url,
    url,
    name: title,
    description: property.description || `${title} in ${property.community}, Dubai.`,
    image: images.slice(0, 8).map(absoluteUrl),
    datePosted: property.publishedAt,
    ...(property.updatedAt ? { dateModified: property.updatedAt } : {}),
    provider: { "@id": ORG_ID },
    offers: {
      "@type": "Offer",
      price: property.price,
      priceCurrency: "AED",
      availability: LISTING_AVAILABILITY[property.status],
      ...(property.pricePeriod
        ? {
            priceSpecification: {
              "@type": "UnitPriceSpecification",
              price: property.price,
              priceCurrency: "AED",
              billingIncrement: 1,
              unitCode: property.pricePeriod === "yearly" ? "ANN" : "MON",
            },
          }
        : {}),
    },
    about: {
      "@type": property.propertyType === "villa" || property.propertyType === "townhouse"
        ? "SingleFamilyResidence"
        : "Residence",
      name: title,
      numberOfRooms: property.bedrooms,
      numberOfBathroomsTotal: property.bathrooms,
      floorSize: {
        "@type": "QuantitativeValue",
        value: property.area,
        unitCode: "FTK",
      },
      address: {
        "@type": "PostalAddress",
        streetAddress: property.address || property.subCommunity || property.community,
        addressLocality: property.community,
        addressRegion: property.emirate,
        addressCountry: "AE",
      },
    },
  };
}

export function blogListingLd(articles: InsightArticle[]): object {
  return {
    "@context": "https://schema.org",
    "@type": "Blog",
    "@id": `${origin()}/insights`,
    url: `${origin()}/insights`,
    name: "Hive Homes Insights",
    publisher: { "@id": ORG_ID },
    blogPost: articles.map((article) => ({
      "@type": "BlogPosting",
      headline: article.title,
      url: `${origin()}/insights/${article.slug}`,
      image: absoluteUrl(article.image),
      datePublished: article.date,
      articleSection: article.category,
    })),
  };
}

export function blogPostingLd(article: InsightArticle): object {
  const url = `${origin()}/insights/${article.slug}`;
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": url,
    mainEntityOfPage: url,
    headline: article.title,
    description: article.seoDescription || article.excerpt,
    image: absoluteUrl(article.image),
    datePublished: article.date,
    dateModified: article.date,
    articleSection: article.category,
    author: { "@id": ORG_ID, "@type": "Organization", name: "Hive Homes" },
    publisher: { "@id": ORG_ID },
  };
}
